import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMediaQuery, useTheme } from "@mui/material";
import FooterManual from "./footerManual";
import Notes from "./header";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED/LIGHT RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";
const EXTRA_LIGHT_RED_HOVER = "#FFF5F5";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

// Helper component for creating the data cards
const InfoCard = ({ title, icon, children }) => (
    <Box
        sx={{
            border: `2px solid ${DARK_RED_ACCENT}`,
            marginTop: '20px',
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: `0 4px 12px rgba(220, 38, 38, 0.15)`,
            transition: "transform 0.3s ease-in-out",
            '&:hover': {
                transform: "translateY(-5px)",
                boxShadow: `0 10px 20px rgba(220, 38, 38, 0.25)`,
            }
        }}
    >
        <Box
            sx={{
                backgroundColor: RED,
                color: "white",
                padding: "15px 20px",
                borderBottom: `2px solid ${DARK_RED_ACCENT}`
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center' }}>
                <i className={`fas fa-${icon}`} style={{ marginRight: '10px' }}></i>
                {title}
            </Typography>
        </Box>
        <Box sx={{ padding: "20px", backgroundColor: "#fff" }}>
            {children}
        </Box>
    </Box>
);

// Helper component for styled list/detail items
const DetailItem = ({ label, value, labelColor, hoverColor }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "flex-start",
            padding: "10px 8px",
            borderBottom: "1px solid #E5E7EB",
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: hoverColor || EXTRA_LIGHT_RED_HOVER },
            transition: 'background-color 0.2s'
        }}
    >
        <Typography sx={{
            fontWeight: "600",
            minWidth: { xs: "110px", sm: "140px" },
            color: labelColor || RED,
            fontSize: "14px",
            flexShrink: 0
        }}>
            {label}:
        </Typography>
        <Typography sx={{ color: BLACK_TEXT, flex: 1, fontSize: "14px", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
            {value}
        </Typography>
    </Box>
);

const PrivacyPolicy = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    return (
        <Box
            className="container-main-AllSeeModels"
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#fff"
            }}
        >
            {/* Header/Notes Component */}
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Banner Section */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
                    backgroundColor: "black",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src="/image/AllSeeModelsBackgroungCar.png"
                    alt="Automotive Background"
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.35,
                    }}
                />

                <Typography
                    sx={{
                        position: "relative",
                        color: "white",
                        fontWeight: "900",
                        textTransform: "uppercase",
                        letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
                        fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
                        zIndex: 2,
                        textShadow: "0 0 10px rgba(0,0,0,0.8)",
                        textAlign: "center",
                        lineHeight: 1.1,
                        px: "20px",
                    }}
                >
                    Privacy & Cookie Policy
                </Typography>
            </Box>

            {/* Main Content Container */}
            <Box
                sx={{
                    width: isMobile ? "95%" : isTablet ? "92%" : "85%",
                    backgroundColor: "#FFFFFF",
                    margin: "0 auto",
                    marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
                    borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
                    padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
                    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
                    zIndex: 10,
                    position: "relative",
                }}
            >
                {/* Introduction Section */}
                <Box sx={{ textAlign: "center", marginBottom: isMobile ? "35px" : isTablet ? "45px" : "60px" }}>
                    <Typography
                        sx={{
                            fontWeight: "800",
                            color: DARK_RED_ACCENT,
                            marginBottom: isMobile ? "10px" : "12px",
                            textTransform: "uppercase",
                            fontSize: isMobile ? "18px" : isTablet ? "24px" : "32px",
                            lineHeight: 1.3,
                        }}
                    >
                        Our Commitment to Your Privacy
                    </Typography>
                    <Typography
                        sx={{
                            color: GRAY_TEXT,
                            fontSize: isMobile ? "13px" : isTablet ? "14px" : "16px",
                            maxWidth: "900px",
                            margin: "0 auto",
                            lineHeight: "1.7",
                            px: isMobile ? "12px" : "0",
                        }}
                    >
                        Amar Enterprises Co Ltd ("we," "us," or "our") is dedicated to safeguarding your personal information. This policy outlines our collection, use, disclosure, and security practices, fully <b>compliant with Japan's Act on the Protection of Personal Information (APPI).</b>
                    </Typography>
                </Box>

                {/* Effective Date Box */}
                <Box 
                    sx={{
                        backgroundColor: LIGHT_RED_BG,
                        border: `2px solid ${RED}`,
                        borderRadius: isMobile ? "8px" : "12px",
                        padding: isMobile ? "15px" : isTablet ? "18px" : "20px",
                        marginBottom: isMobile ? "30px" : isTablet ? "40px" : "50px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "700",
                            color: RED,
                            fontSize: isMobile ? "14px" : "16px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: "8px",
                        }}
                    >
                        <i className="fas fa-calendar-alt" style={{ fontSize: isMobile ? "16px" : "18px" }}></i>
                        Effective Date: 03-11-2025
                    </Typography>
                </Box>

                {/* Two-Column Grid for Primary Information & Rights */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: isMobile ? "20px" : isTablet ? "24px" : "30px",
                        marginBottom: isMobile ? "25px" : isTablet ? "35px" : "40px"
                    }}
                >
                    {/* Column 1: Contact & APPI Status */}
                    <InfoCard title="Commitment & Contact Information" icon="id-card">
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Legal Status</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Personal Information Handling Business Operator (APPI Compliant)</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Director</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Raj Kumar Sharma</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Address</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>1076-1 Ooaza Komizou Iwatsuki Saitama-Shi Saitama-Ken - 339-0003, Japan</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>License</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Saitama Prefectural Public Safety Commission No. 431260012433, Qualification Registration Number: T7-0300-0202-3122</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Privacy Email</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>sales@amar.co.jp</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Privacy Mobile</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>+81-48-793-3911</Typography>
                            </Box>
                        </Box>
                    </InfoCard>

                    {/* Column 2: Data Subject Rights */}
                    <InfoCard title="Your Data Subject Rights (APPI)" icon="user-cog">
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "12px" : "15px", marginBottom: "15px" }}>
                            You have the right to manage your retained personal data. Contact us to exercise any of these rights:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Disclosure (Access)</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Request a copy of the personal data we hold about you.</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Correction/Deletion</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Request correction or deletion of inaccurate or incomplete data.</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Stop Use/Provision</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Request to cease use/provision of your data, especially for direct marketing.</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Withdrawal of Consent</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Withdraw your consent where consent was the basis for processing.</Typography>
                            </Box>
                        </Box>
                    </InfoCard>
                </Box>

                {/* Data Collection & Purpose */}
                <InfoCard title="Data We Collect and Purpose of Use" icon="database">
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr" },
                            gap: "10px",
                            backgroundColor: LIGHT_RED_BG,
                            padding: "10px",
                            borderRadius: "5px",
                            marginBottom: "15px"
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "13px" : "14px" }}>Category of Data</Typography>
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "13px" : "14px" }}>Purpose of Use (APPI Compliance)</Typography>
                    </Box>

                    {[
                        {
                            cat: 'A. Identification & Contact',
                            data: 'Name, address, email, phone/FAX.',
                            purpose: 'Contract Fulfillment: To process orders and manage global Import & Export logistics.'
                        },
                        {
                            cat: 'B. Transaction & Service',
                            data: 'Payment details, transaction history, inquiry content.',
                            purpose: 'Customer Service: To respond to inquiries and provide timely support.'
                        },
                        {
                            cat: 'C. Vehicle & Export',
                            data: 'Chassis/engine number, mileage, de-registration records.',
                            purpose: 'Legal Compliance: To comply with Japanese export regulations and provide necessary documentation.'
                        },
                        {
                            cat: 'D. Marketing & Analytics',
                            data: 'Purchase history, website usage data.',
                            purpose: 'Legitimate Business Interest: To send updates, conduct market analysis, and improve our services.'
                        },
                        {
                            cat: 'E. Yahoo Auction',
                            data: 'Bidding records, user identity verification.',
                            purpose: 'Auction Facilitation: To register bidders and finalize sales on the Yahoo Auction platform.'
                        },
                    ].map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr" },
                                gap: "10px",
                                padding: "10px",
                                borderBottom: index < 4 ? "1px solid #E5E7EB" : 'none',
                                '&:hover': { backgroundColor: LIGHT_RED_BG }
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: RED, fontSize: isMobile ? "13px" : "14px" }}>{item.cat}</Typography>
                                <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "11px" : "12px" }}>({item.data})</Typography>
                            </Box>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "13px" : "14px" }}>{item.purpose}</Typography>
                        </Box>
                    ))}
                </InfoCard>

                {/* Cookies and Third-Party Transfer Section */}
                <Box
                    sx={{
                        marginTop: isMobile ? "20px" : isTablet ? "25px" : "30px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: isMobile ? "20px" : isTablet ? "24px" : "30px",
                    }}
                >
                    <InfoCard title="Cookies and Tracking Technologies" icon="cookie-bite">
                        <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '10px', fontSize: isMobile ? "12px" : "14px" }}>Overview:</Typography>
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "15px", lineHeight: "1.5" }}>
                            Cookies are small data files that recognize you on subsequent visits. Your use of our website signifies your <b>acknowledgment and agreement</b> to their use for a unique browsing experience, site improvement, and performance analysis.
                        </Typography>
                        <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '5px', fontSize: isMobile ? "12px" : "14px" }}>Blocking Cookies:</Typography>
                        <Typography sx={{ color: RED, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                            <b>Warning:</b> If you block our use of cookies via your browser settings, the functionality of this website can be restricted and may affect your user experience.
                        </Typography>
                    </InfoCard>

                    <InfoCard title="Data Disclosure & Cross-Border Transfer" icon="globe">
                        <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '10px', fontSize: isMobile ? "12px" : "14px" }}>Third-Party Sharing:</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "10px" : "12px", marginBottom: "15px" }}>
                            {[
                                { purpose: "Site Usage Analysis", providers: "Google, Outsourcing Companies" },
                                { purpose: "Advertisement Delivery (Targeting)", providers: "Google, Bing, Meta (Facebook, Instagram), Twitter" },
                                { purpose: "Sales Activities", providers: "Google, Bing, Meta, Twitter, Outsourcing Companies" },
                            ].map((item, index) => (
                                <Box key={index}>
                                    <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>{item.purpose}</Typography>
                                    <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>{item.providers}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginTop: '15px', marginBottom: '10px', fontSize: isMobile ? "12px" : "14px" }}>Cross-Border Transfer:</Typography>
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "5px", lineHeight: "1.5" }}>
                            Your data will be transferred outside Japan (to destination and transit countries).
                        </Typography>
                        <Typography sx={{ color: RED, fontSize: isMobile ? "12px" : "14px", fontWeight: "600", lineHeight: "1.5" }}>
                            Explicit consent is obtained for transfers to countries without adequate data protection. Contractual safeguards ensure equivalent APPI protection standards.
                        </Typography>
                    </InfoCard>
                </Box>

                {/* Data Security & Retention */}
                <InfoCard title="Data Security and Retention" icon="lock">
                    <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "15px" : "20px" }}>
                        <Box>
                            <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "8px" }}>Security Measures</Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                                We implement appropriate technical and organizational measures (consistent with our 'Systems Driven Process') to protect your information against unauthorized access, loss, destruction, or misuse.
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "8px" }}>Data Retention</Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                                Data is retained only as long as necessary to fulfill the purposes outlined or for the longer period required to comply with our legal, accounting, and regulatory obligations (e.g., maintaining transaction records for tax and legal purposes).
                            </Typography>
                        </Box>
                    </Box>
                </InfoCard>
            </Box>

            {/* Space Filler for Footer Alignment */}
            <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />

            {/* Footer Component */}
            <FooterManual />
        </Box>
    );
};

export default PrivacyPolicy;